import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Save, Loader2, CreditCard, Mail, Globe, CheckCircle2, XCircle,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsTab({ adminToken }) {
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stripeKey, setStripeKey] = useState("");
  const [savingStripe, setSavingStripe] = useState(false);

  const fetchSettings = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${adminToken}` } }); setSettings(res.data); }
    catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [adminToken]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        site_name: settings.site_name,
        google_auth_enabled: settings.google_auth_enabled,
        default_currency: settings.default_currency,
        free_audit_limit: settings.free_audit_limit,
      }, { headers: getHeaders() });
      toast.success("Settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const handleSaveStripe = async () => {
    if (!stripeKey.trim()) { toast.error("Enter a Stripe API key"); return; }
    setSavingStripe(true);
    try { await axios.put(`${API}/admin/settings/stripe`, { stripe_key: stripeKey.trim() }, { headers: getHeaders() }); toast.success("Stripe key updated"); setStripeKey(""); fetchSettings(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSavingStripe(false); }
  };

  if (!settings) return null;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader><CardTitle className="text-lg text-[#111827]">Platform Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2"><Label className="text-slate-700">Site Name</Label><Input value={settings.site_name || ""} onChange={(e) => setSettings({...settings, site_name: e.target.value})} className="bg-white border-slate-300" data-testid="site-name-input" /></div>
          <div className="space-y-2"><Label className="text-slate-700">Default Currency</Label>
            <Select value={settings.default_currency || "usd"} onValueChange={(v) => setSettings({...settings, default_currency: v})}>
              <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="usd">USD ($)</SelectItem><SelectItem value="eur">EUR</SelectItem><SelectItem value="gbp">GBP</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between"><Label className="text-slate-700">Google Auth</Label><Switch checked={settings.google_auth_enabled ?? true} onCheckedChange={(v) => setSettings({...settings, google_auth_enabled: v})} data-testid="google-auth-toggle" /></div>
          <Button onClick={handleSave} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Settings
          </Button>
        </CardContent>
      </Card>
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader><CardTitle className="text-lg text-[#111827]">Integrations</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[
            { name: "Stripe", icon: CreditCard, color: "purple", connected: settings.stripe_connected, desc: "Payments" },
            { name: "Resend", icon: Mail, color: "blue", connected: settings.resend_connected, desc: "Emails" },
            { name: "Google Auth", icon: Globe, color: "emerald", connected: settings.google_auth_enabled, desc: "Social login" },
          ].map((int, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <int.icon className={`w-5 h-5 text-${int.color}-500`} />
                <div><p className="text-sm font-medium text-[#111827]">{int.name}</p><p className="text-xs text-[#9CA3AF]">{int.desc}</p></div>
              </div>
              {int.connected ? <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" />Connected</Badge> : <Badge className="bg-red-50 text-red-600 border-red-200"><XCircle className="w-3 h-3 mr-1" />Disabled</Badge>}
            </div>
          ))}
          <div className="p-4 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 mb-2"><KeyRound className="w-4 h-4 text-purple-500" /><Label className="text-sm font-medium text-[#111827]">Stripe API Key</Label></div>
            <p className="text-xs mb-2 text-[#9CA3AF]">Enter your Stripe secret key (sk_live_... or sk_test_...)</p>
            <div className="flex gap-2">
              <Input type="password" value={stripeKey} onChange={e => setStripeKey(e.target.value)} placeholder="sk_test_..." className="flex-1 bg-white border-slate-300" data-testid="stripe-key-input" />
              <Button onClick={handleSaveStripe} disabled={savingStripe} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-stripe-key-btn">
                {savingStripe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
